import React, { useMemo, useRef } from 'react';
import './OolaWheelClassicV2.css';

const SIZE = 400;
const CX = 200;
const CY = 200;
const HUB = 46;
const MAX_R = 165;
const GAP = 10;
const SEG = 360 / 7 - GAP;
const GHOST_R = 118;
const LABEL_R = 188;

function polar(cx, cy, r, deg) {
    const a = ((deg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

/** Classic TotalScore → radius (Slices.js) */
function scoreRadius(totalScore) {
    if (totalScore == null || totalScore === 0 || Number.isNaN(Number(totalScore))) {
        return null;
    }
    const t = Number(totalScore);
    const factor = t * 0.005 + (t >= 70 ? 0.55 : 0.44);
    return Math.min(MAX_R, Math.max(HUB + 36, factor * 170));
}

/** Soft coxcomb petal — annular sector only (no pie from center) */
function petal(cx, cy, r0, r1, a0, a1) {
    if (!r1 || r1 <= r0 + 4) return null;
    const d = ((a1 - a0) + 360) % 360;
    const large = d > 180 ? 1 : 0;
    const p0 = polar(cx, cy, r1, a0);
    const p1 = polar(cx, cy, r1, a1);
    const p2 = polar(cx, cy, r0, a1);
    const p3 = polar(cx, cy, r0, a0);
    return `M ${p0.x} ${p0.y} A ${r1} ${r1} 0 ${large} 1 ${p1.x} ${p1.y} L ${p2.x} ${p2.y} A ${r0} ${r0} 0 ${large} 0 ${p3.x} ${p3.y} Z`;
}

function sumQuestions(f) {
    return (f.questions || []).reduce(
        (pv, cv) => pv + (cv.score != null ? Number(cv.score) : 0),
        0
    );
}

function resolveTotal(f) {
    if (f.TotalScore != null && !Number.isNaN(Number(f.TotalScore))) {
        return Number(f.TotalScore);
    }
    const q = sumQuestions(f);
    return q > 0 ? q : null;
}

function computeOolaScore(fs) {
    let total = 0;
    for (const f of fs) {
        const totalF = sumQuestions(f);
        total += totalF + totalF * (f.weight || 0);
    }
    return Math.round(total);
}

/**
 * Portal V2 coxcomb wheel — only F petals + hub. No guide rings / spokes.
 * variant="section" — milestone emphasis for single-F completion.
 */
export default function OolaWheelClassicV2({
    Fs = [],
    activeIndex = 0,
    variant = 'default',
}) {
    const isSection = variant === 'section';
    const uid = useRef(`cx${Math.random().toString(36).slice(2, 8)}`).current;

    const { slices, oola, showScore } = useMemo(() => {
        const slicesOut = Fs.map((f, i) => {
            const total = resolveTotal(f);
            const done = total != null;
            const r = done ? scoreRadius(total) : null;
            const a0 = i * (360 / 7) + GAP / 2;
            const a1 = a0 + SEG;
            const mid = (a0 + a1) / 2;
            const labelR = done ? (HUB + r) / 2 : null;
            const outer = polar(CX, CY, LABEL_R, mid);
            return {
                id: f.id,
                name: f.name,
                color: f.color || '#15c285',
                index: i,
                done,
                shown: done ? (total / 10).toFixed(1) : null,
                isActive: i === activeIndex,
                d: done ? petal(CX, CY, HUB, r, a0, a1) : null,
                ghost: !done ? petal(CX, CY, HUB, GHOST_R, a0, a1) : null,
                label: labelR ? polar(CX, CY, labelR, mid) : null,
                outer,
                mid,
            };
        });

        return {
            slices: slicesOut,
            oola: computeOolaScore(Fs),
            showScore:
                slicesOut.filter((s) => s.done).length >= 7 || activeIndex >= 6,
        };
    }, [Fs, activeIndex]);

    return (
        <div
            className={`pv2-cwheel${isSection ? ' pv2-cwheel--section' : ''}`}
            aria-label="Oola Wheel"
        >
            <div className="pv2-cwheel__glow" aria-hidden="true" />
            <svg className="pv2-cwheel__svg" viewBox={`0 0 ${SIZE} ${SIZE}`}>
                <defs>
                    <filter id={`${uid}-g`} x="-40%" y="-40%" width="180%" height="180%">
                        <feGaussianBlur stdDeviation="3.2" result="b" />
                        <feMerge>
                            <feMergeNode in="b" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    <filter id={`${uid}-pulse`} x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="5" result="b" />
                        <feMerge>
                            <feMergeNode in="b" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    <radialGradient id={`${uid}-hub`} cx="40%" cy="32%" r="68%">
                        <stop offset="0%" stopColor="rgba(255,255,255,0.16)" />
                        <stop offset="100%" stopColor="rgba(6,8,12,0.94)" />
                    </radialGradient>
                    {slices.map((s) => (
                        <linearGradient
                            key={s.id}
                            id={`${uid}-${s.index}`}
                            x1="0%"
                            y1="0%"
                            x2="80%"
                            y2="100%"
                        >
                            <stop offset="0%" stopColor={s.color} stopOpacity="1" />
                            <stop
                                offset="100%"
                                stopColor={s.color}
                                stopOpacity={s.isActive ? '0.78' : '0.58'}
                            />
                        </linearGradient>
                    ))}
                </defs>

                {/* Soft ambient disc */}
                <circle
                    cx={CX}
                    cy={CY}
                    r={MAX_R + 8}
                    fill="rgba(255,255,255,0.02)"
                    stroke="rgba(255,255,255,0.06)"
                    strokeWidth="1"
                />

                {slices.map((s) =>
                    s.ghost ? (
                        <path
                            key={`g-${s.id}`}
                            d={s.ghost}
                            className="pv2-cwheel__ghost"
                            fill={s.color}
                            fillOpacity={0.07}
                            stroke={s.color}
                            strokeOpacity={0.22}
                            strokeWidth="1"
                        />
                    ) : null
                )}

                {slices.map((s, i) =>
                    s.d ? (
                        <g
                            key={s.id}
                            className={`pv2-cwheel__petal${
                                s.isActive ? ' is-active' : ''
                            }${s.done && !s.isActive ? ' is-done' : ''}`}
                            style={{ ['--d']: `${0.06 + i * 0.08}s` }}
                        >
                            <path
                                d={s.d}
                                fill={`url(#${uid}-${s.index})`}
                                filter={
                                    s.isActive
                                        ? `url(#${uid}-pulse)`
                                        : undefined
                                }
                                className="pv2-cwheel__petal-fill"
                                opacity={s.isActive ? 1 : s.done ? 0.72 : 1}
                            />
                            {!isSection && s.label && s.shown ? (
                                <text
                                    x={s.label.x}
                                    y={s.label.y}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    className="pv2-cwheel__n"
                                >
                                    {s.shown}
                                </text>
                            ) : null}
                        </g>
                    ) : null
                )}

                {isSection
                    ? slices.map((s) => (
                          <g
                              key={`lbl-${s.id}`}
                              className={`pv2-cwheel__out${
                                  s.isActive ? ' is-active' : ''
                              }${s.done ? ' is-done' : ''}`}
                              style={{ ['--d']: `${0.35 + s.index * 0.05}s` }}
                          >
                              <text
                                  x={s.outer.x}
                                  y={s.outer.y - (s.done ? 6 : 0)}
                                  textAnchor="middle"
                                  dominantBaseline="middle"
                                  className="pv2-cwheel__out-name"
                                  fill={s.color}
                              >
                                  {s.name}
                              </text>
                              {s.done && s.shown ? (
                                  <text
                                      x={s.outer.x}
                                      y={s.outer.y + 10}
                                      textAnchor="middle"
                                      dominantBaseline="middle"
                                      className="pv2-cwheel__out-score"
                                      fill={s.color}
                                  >
                                      {s.shown}
                                  </text>
                              ) : null}
                          </g>
                      ))
                    : null}

                <circle
                    cx={CX}
                    cy={CY}
                    r={HUB - 4}
                    fill={`url(#${uid}-hub)`}
                    stroke="rgba(239,208,120,0.32)"
                    strokeWidth="1.25"
                    className="pv2-cwheel__hub"
                />

                {showScore ? (
                    <>
                        <text x={CX} y={CY - 7} textAnchor="middle" className="pv2-cwheel__k">
                            OOLA SCORE
                        </text>
                        <text x={CX} y={CY + 15} textAnchor="middle" className="pv2-cwheel__v">
                            {oola}
                        </text>
                    </>
                ) : (
                    <text x={CX} y={CY + 6} textAnchor="middle" className="pv2-cwheel__brand">
                        Oola.
                    </text>
                )}
            </svg>

            {!isSection ? (
                <ul className="pv2-cwheel__legend">
                    {slices.map((s) => (
                        <li
                            key={s.id}
                            className={s.isActive ? 'is-on' : ''}
                            style={{ color: s.color }}
                        >
                            <i style={{ background: s.color }} />
                            {s.name}
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
}
